import { useEffect, useState, type RefObject } from "react";

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

type Props = {
  audioRef: RefObject<HTMLAudioElement | null>;
  /** Optional second track (e.g. cloned-voice TTS) kept in sync with the main mix. */
  vocalRef?: RefObject<HTMLAudioElement | null>;
};

function syncVocalToMain(
  main: HTMLAudioElement,
  vocal: HTMLAudioElement | null,
) {
  if (!vocal?.src) return;
  const d = Math.abs(vocal.currentTime - main.currentTime);
  if (d > 0.12) vocal.currentTime = main.currentTime;
}

/**
 * Play / pause, elapsed & total duration, and scrub bar for the hidden `<audio>` mix player.
 */
export function MusicTransport({ audioRef, vocalRef }: Props) {
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [hasTrack, setHasTrack] = useState(false);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const syncDuration = () => {
      const d = el.duration;
      setDuration(Number.isFinite(d) && d > 0 ? d : 0);
    };

    const syncTrackPresence = () => {
      setHasTrack(Boolean(el.src && el.currentSrc));
    };

    const onTime = () => {
      setCurrent(el.currentTime);
      if (vocalRef?.current) syncVocalToMain(el, vocalRef.current);
    };
    const onPlay = () => {
      setPlaying(true);
      const v = vocalRef?.current;
      if (v?.src) {
        v.currentTime = el.currentTime;
        void v.play().catch(() => undefined);
      }
    };
    const onPause = () => {
      setPlaying(false);
      vocalRef?.current?.pause();
    };
    const onEnded = () => {
      setPlaying(false);
      setCurrent(0);
      if (vocalRef?.current) {
        vocalRef.current.pause();
        vocalRef.current.currentTime = 0;
      }
    };

    const onEmptied = () => setHasTrack(false);

    syncDuration();
    syncTrackPresence();
    setCurrent(el.currentTime);
    setPlaying(!el.paused);

    el.addEventListener("loadedmetadata", syncDuration);
    el.addEventListener("durationchange", syncDuration);
    el.addEventListener("loadeddata", syncTrackPresence);
    el.addEventListener("loadstart", syncTrackPresence);
    el.addEventListener("emptied", onEmptied);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);

    return () => {
      el.removeEventListener("loadedmetadata", syncDuration);
      el.removeEventListener("durationchange", syncDuration);
      el.removeEventListener("loadeddata", syncTrackPresence);
      el.removeEventListener("loadstart", syncTrackPresence);
      el.removeEventListener("emptied", onEmptied);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, [audioRef, vocalRef]);

  const togglePlayPause = () => {
    const el = audioRef.current;
    if (!el?.src) return;
    const v = vocalRef?.current;
    if (el.paused) {
      if (v?.src) v.currentTime = el.currentTime;
      void el.play().catch(() => undefined);
      if (v?.src) void v.play().catch(() => undefined);
    } else {
      el.pause();
      v?.pause();
    }
  };

  const seek = (value: number) => {
    const el = audioRef.current;
    if (!el || !Number.isFinite(value)) return;
    const t = Math.min(Math.max(0, value), duration || value);
    el.currentTime = t;
    const v = vocalRef?.current;
    if (v?.src) v.currentTime = t;
    setCurrent(el.currentTime);
  };

  const max = Math.max(duration, 0.01);
  const durationDisplay =
    duration > 0 ? formatTime(duration) : hasTrack ? "…" : "—:—";

  return (
    <div className="music-transport" aria-label="Mix playback">
      <button
        type="button"
        className="btn-play-pause"
        onClick={togglePlayPause}
        disabled={!hasTrack}
        aria-label={playing ? "Pause" : "Play"}
        title={!hasTrack ? "Load a mix with “Play last mix”" : playing ? "Pause" : "Play"}
      >
        {playing ? (
          <span className="btn-play-pause__icon" aria-hidden>
            ❚❚
          </span>
        ) : (
          <span className="btn-play-pause__icon btn-play-pause__icon--play" aria-hidden>
            ▶
          </span>
        )}
      </button>

      <div className="music-transport__timeline">
        <input
          type="range"
          className="music-scrub"
          min={0}
          max={max}
          step={0.05}
          value={Math.min(current, max)}
          disabled={!hasTrack || duration <= 0}
          aria-label="Seek in mix"
          onChange={(e) => seek(parseFloat(e.target.value))}
        />
        <div className="music-transport__times">
          <span className="music-time music-time--current">{formatTime(current)}</span>
          <span className="music-time music-time--sep" aria-hidden>
            /
          </span>
          <span className="music-time music-time--total">{durationDisplay}</span>
        </div>
      </div>
    </div>
  );
}
